import {
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  chakra,
} from "@chakra-ui/react";
import { EventDetailTimelinesSection } from "@common/interfaces/KYDEvent";
import dayjs from "dayjs";

import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TimelinesSchema } from "./schema";
import TimelineDatepickerFormInput from "./TimelineDatepickerFormInput";
import KYDButton from "src/ui/KYDButton";
import { Dayjs } from "dayjs";
import { useMemo } from "react";

const KydForm = chakra(chakra.form);

const EditTimelineModal = ({
  data,
  onCancel,
  onSave,
  isOpen = false,
  isLoading,
  isEventLive,
}: {
  data?: EventDetailTimelinesSection["data"];
  onCancel: () => void;
  onSave: (data: any) => Promise<any>;
  isOpen: boolean;
  isLoading: boolean;
  isEventLive: boolean;
}) => {
  const methods = useForm({
    resolver: yupResolver(TimelinesSchema),
    mode: "onChange",
    criteriaMode: "all",
    reValidateMode: "onChange",
    /*
    For context:
    - Announce at (before or equal to on sale)
    - On sale (can be entire duration until end date)
    - Door open (has to be before or equal event start date)
    - event start date (before end date && after or equal to doors open)
    - off sale (after on sale)
    - event end date (after or equal to off sale)
    */
    values: {
      announce_at: data.announce_at
        ? dayjs(data.announce_at).tz(data.timezone).toDate()
        : null,
      on_sale_at: data.on_sale_at
        ? dayjs(data.on_sale_at).tz(data.timezone).toDate()
        : null,
      off_sale_at: data.off_sale_at
        ? dayjs(data.off_sale_at).tz(data.timezone).toDate()
        : null,
      start_at: data.start_at
        ? dayjs(data.start_at).tz(data.timezone).toDate()
        : null,
      end_at: data.end_at
        ? dayjs(data.end_at || new Date())
            .tz(data.timezone)
            .toDate()
        : null,
      timezone: data.timezone,
    },
  });
  const {
    reset,
    formState: { isValid, isDirty: isFormDirty, dirtyFields },
    handleSubmit,
    setValue,
    watch,
    trigger,
  } = methods;

  const onSubmit: SubmitHandler<any> = (data) => {
    onSave(data).then(() => {
      reset();
    });
  };

  const onCloseModal = () => {
    onCancel();
    reset();
  };

  const startAt = watch("start_at");
  const endAt = watch("end_at");
  const startAtIsInThePast = Boolean(startAt && dayjs(startAt).isBefore(dayjs()));

  // TODO: uncomment to launch the rescheduling campaign modal
  // const hasStartAtChanged = !dayjs(startAt).isSame(
  //   dayjs(data.start_at),
  //   "millisecond"
  // );

  const hasStartAtChanged = false;

  const highlights = [
    {
      date: dayjs(watch("announce_at")).tz(data.timezone).format("DD/MM/YYYY"),
      color: "black",
      label: "📢",
    },
    {
      date: dayjs(startAt).tz(data.timezone).format("DD/MM/YYYY"),
      color: "black",
      label: "🎬",
    },
    {
      date: dayjs(endAt).tz(data.timezone).format("DD/MM/YYYY"),
      color: "black",
      label: "🏁",
    },
    {
      date: dayjs(watch("on_sale_at")).tz(data.timezone).format("DD/MM/YYYY"),
      color: "green.500",
      label: "💰",
    },
    {
      date: dayjs(watch("off_sale_at")).tz(data.timezone).format("DD/MM/YYYY"),
      color: "red.500",
      label: "🛑",
    },
  ].reduce((highlights, occurrence) => {
    const exists = highlights[occurrence.date];

    const newOccurence = exists
      ? {
          ...exists,
          label: `${exists.label}${occurrence.label}`,
        }
      : occurrence;

    if (Array.from(newOccurence.label).length > 2) {
      newOccurence.label = Array.from(newOccurence.label).length;
    }

    return {
      ...highlights,
      [occurrence.date]: newOccurence,
    };
  }, {});

  const endAtIsNotDefault = useMemo(() => {
    if (!startAt || !endAt) return false;

    const expectedEndAt = dayjs(startAt).add(5, "hours");
    const actualEndAt = dayjs(endAt);

    return !expectedEndAt.isSame(actualEndAt, "minute");
  }, [startAt, endAt]);

  return (
    <Modal
      size={"xl"}
      isOpen={isOpen}
      onClose={onCloseModal}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent borderRadius="20px">
        <KydForm w="full" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader height={"34px"}>Edit timeline</ModalHeader>
          <ModalBody>
            <FormProvider {...methods}>
              <Text width={"full"}>Set the timing of the event</Text>
              <VStack
                direction={{ base: "column", md: "row" }}
                w="100%"
                alignItems={"flex-start"}
                spacing={{ base: 26 }}
                mt="32px"
              >
                <Stack
                  spacing={{ base: 6 }}
                  direction={["column", "row"]}
                  w="full"
                >
                  <TimelineDatepickerFormInput
                    dateTimeProps={{
                      disablePast: true,
                      disabled: isLoading,
                      closeOnSelect: true,
                    }}
                    onChange={(date: string) => {
                      setValue("end_at", dayjs(date).add(5, "hours").toDate(), {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                      // Trigger validation for start_at after end_at is updated
                      // This ensures start_at validates against the new end_at value
                      setTimeout(() => trigger("start_at"), 0);
                    }}
                    validationFailMessage="Needs to be before event end, and equal to or after doors open."
                    deps={["end_at", "doors_at"]}
                    name="start_at"
                    label="🎬 Event start"
                    required
                    timezone={data.timezone}
                    highlights={highlights}
                    info={
                      startAtIsInThePast
                        ? "Start date is in the past"
                        : isEventLive && hasStartAtChanged
                          ? "All attendees will be notified of start time change"
                          : ""
                    }
                  />
                  <TimelineDatepickerFormInput
                    dateTimeProps={{
                      disablePast: true,
                      disabled: isLoading,
                      closeOnSelect: true,
                    }}
                    validationFailMessage="Needs to be after off sale."
                    deps={["off_sale_at", "start_at"]}
                    name="end_at"
                    label="🏁 Event end"
                    required
                    timezone={data.timezone}
                    highlights={highlights}
                    subtitle={
                      endAtIsNotDefault
                        ? "Custom timing -- if event start is changed, event end will update automatically"
                        : "Defaults to 5 hours after the event start and updates automatically when event start is updated"
                    }
                  />
                </Stack>
                <Stack w="full">
                  <TimelineDatepickerFormInput
                    dateTimeProps={{
                      disabled: isLoading,
                    }}
                    deps={["on_sale_at"]}
                    validationFailMessage="Needs to be before or equal to on sale."
                    name="announce_at"
                    label="📢 Announce on"
                    required
                    timezone={data.timezone}
                    highlights={highlights}
                  />
                </Stack>
                <Stack
                  spacing={{ base: 6 }}
                  direction={["column", "row"]}
                  w="full"
                >
                  <TimelineDatepickerFormInput
                    dateTimeProps={{
                      disabled: isLoading,
                    }}
                    validationFailMessage="Needs to be after announce, and less than or equal to off sale."
                    deps={["off_sale_at", "announce_at"]}
                    name="on_sale_at"
                    label="💰 On sale"
                    timezone={data.timezone}
                    highlights={highlights}
                  />
                  <TimelineDatepickerFormInput
                    dateTimeProps={{
                      disabled: isLoading,
                    }}
                    validationFailMessage="Needs to be after on sale, and equal to or before event end."
                    deps={["end_at", "on_sale_at"]}
                    name="off_sale_at"
                    label="🛑 Off sale"
                    timezone={data.timezone}
                    highlights={highlights}
                  />
                </Stack>
              </VStack>
            </FormProvider>
          </ModalBody>
          <ModalFooter mt="8px">
            <HStack>
              <KYDButton
                variant="outline"
                width={120}
                isLoading={isLoading}
                onClick={onCloseModal}
                mr="4"
              >
                Cancel
              </KYDButton>
              <KYDButton
                isLoading={isLoading}
                isDisabled={!isValid || !isFormDirty || isLoading}
                type="submit"
                onClick={handleSubmit(onSubmit)}
                width={120}
              >
                {isEventLive && hasStartAtChanged ? "Next" : "Save"}
              </KYDButton>
            </HStack>
          </ModalFooter>
        </KydForm>
      </ModalContent>
    </Modal>
  );
};

export default EditTimelineModal;
